// src/components/SocketComponent.js
import { useEffect, useRef } from "react";
import io from "socket.io-client";
import config from "../config";
import { useUser } from "../contexts/UserContext";
const SocketComponent = () => {
  const {
    isAuthenticated,
    token,
    userType,
    setUsersWithActiveChat,
    setUsersWithChatHistory,
    isActiveChat,
    getUsersWithActiveChat,
    getUsersWithChatHistory,
  } = useUser();
  
  // Use refs to store latest callback values to avoid dependency issues
  const getUsersWithActiveChatRef = useRef(getUsersWithActiveChat);
  const getUsersWithChatHistoryRef = useRef(getUsersWithChatHistory);
  const isActiveChatRef = useRef(isActiveChat);
  const userTypeRef = useRef(userType);
  
  // Update refs when values change
  useEffect(() => {
    getUsersWithActiveChatRef.current = getUsersWithActiveChat;
    getUsersWithChatHistoryRef.current = getUsersWithChatHistory;
    isActiveChatRef.current = isActiveChat;
    userTypeRef.current = userType;
  }, [getUsersWithActiveChat, getUsersWithChatHistory, isActiveChat, userType]);

  useEffect(() => {
    if (!isAuthenticated) return;
    
    const socket = io(config.backendUrl, {
      auth: {
        token: token,
      },
    });

    const handleMessage = async (message) => {
      const { identifier, msgBody } = message;
      // this is the first message from user to human
      if (identifier === "startChat") {
        const audio = new Audio("/notification.mp3");
        audio.play();
        // Always update both lists when a new chat starts
        // This ensures the new chat appears immediately regardless of which tab is active
        const activeUsers = await getUsersWithActiveChatRef.current();
        setUsersWithActiveChat(activeUsers);
        const historyUsers = await getUsersWithChatHistoryRef.current();
        setUsersWithChatHistory(historyUsers);
      }


      if (identifier === "activeChat") {
        //if current url is not /chat play notification sound
        const currentPath = window.location.pathname;
        if (currentPath !== "/chat" && currentPath !== "/") {
          const audio = new Audio("/notification.mp3");
          audio.play();
        }
        const userPhone = msgBody.phone;
        const messageToAdd = {
          message: msgBody.message,
          imagePath: msgBody.imagePath || null,
          sentTime: msgBody.sentTime,
          sender: msgBody.sender,
          direction: msgBody.direction,
          opened: msgBody.opened,
        };

        setUsersWithActiveChat((prevUsers) => {
          return prevUsers.map((user) => {
            if (user.phone === userPhone) {
              return {
                ...user,
                messages: [...user.messages, messageToAdd],
              };
            }
            return user;
          });
        });
      }

      if (identifier === "testDriveUpdated") {
        window.dispatchEvent(new CustomEvent("testDriveUpdated", { detail: msgBody }));
      }

      if (identifier === "chatTypeChanged") {
        const { userId, representativeChatType } = msgBody;
        
        // Check if current user is admin/superAdmin or rental
        const currentUserType = userTypeRef.current;
        const isAdmin = Array.isArray(currentUserType) && (
          currentUserType.includes("admin") || currentUserType.includes("superAdmin")
        );
        const isRental = Array.isArray(currentUserType) && currentUserType.includes("rental");
        
        if (isAdmin) {
          // For admins: just update the field in existing lists
          setUsersWithActiveChat((prevUsers) => {
            return prevUsers.map((user) => {
              if (user._id === userId) {
                return {
                  ...user,
                  representativeChatType: representativeChatType,
                };
              }
              return user;
            });
          });
          setUsersWithChatHistory((prevUsers) => {
            return prevUsers.map((user) => {
              if (user._id === userId) {
                return {
                  ...user,
                  representativeChatType: representativeChatType,
                };
              }
              return user;
            });
          });
        } else if (isRental) {
          // For rental users: refetch the lists to see add/remove based on chat type
          if (isActiveChatRef.current) {
            const users = await getUsersWithActiveChatRef.current();
            setUsersWithActiveChat(users);
          } else {
            const users = await getUsersWithChatHistoryRef.current();
            setUsersWithChatHistory(users);
          }
        }
      }
    };

    socket.on("message", handleMessage);

    return () => {
      socket.off("message", handleMessage);
      socket.disconnect();
    };
  }, [isAuthenticated, token, setUsersWithActiveChat, setUsersWithChatHistory]);

  return null;
};

export default SocketComponent;
